import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Row,
    Col,
    Card,
    Statistic,
    Typography,
    Tag,
    Button,
    Table,
    Spin,
    Timeline,
    Layout,
    Tooltip
} from 'antd';
import {
    BarChart,
    Bar,
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer
} from 'recharts';
import adminService from '../../services/adminService';
import DashboardLayout from '../../components/Dashboard/DashboardLayout';
import PageHeader from '../../components/Common/PageHeader';

const { Title, Text } = Typography;
const { Content } = Layout;

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

const AdminDashboard = () => {
    const [loading, setLoading] = useState(true);
    const [analytics, setAnalytics] = useState(null);

    useEffect(() => {
        fetchAnalytics();
    }, []);

    const fetchAnalytics = async () => {
        try {
            const data = await adminService.getAnalytics();
            console.log('Analytics data received:', data);
            setAnalytics(data);
        } catch (error) {
            console.error('Error fetching analytics:', error);
        } finally {
            setLoading(false);
        }
    };



    console.log('Current analytics state:', analytics);

    return (
        <DashboardLayout>
            <Spin spinning={loading}>
            <PageHeader
                title="Admin Dashboard"
                description="Overview of platform statistics, user activity, and auction analytics"
            />
            <Content style={{ padding: window.innerWidth < 768 ? '16px' : '24px' }}>
        <Row gutter={[16, 16]}>

                    {/* Statistics Cards */}
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Users"
                                value={analytics?.total_users || 0}
                                valueStyle={{ color: '#3f8600' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Auctions"
                                value={analytics?.total_auctions || 0}
                                valueStyle={{ color: '#1890ff' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Lots"
                                value={analytics?.total_lots || 0}
                                valueStyle={{ color: '#722ed1' }}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <Card>
                            <Statistic
                                title="Total Bids"
                                value={analytics?.total_bids || 0}
                                valueStyle={{ color: '#fa8c16' }}
                            />
                        </Card>
                    </Col>

                    {/* User Registration Trend */}
                    <Col xs={24} md={12}>
                        <Card 
                            title="User Registration Trend (Last 7 Days)"
                            style={{
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)'
                            }}
                        >
                            {analytics?.user_registration_trend?.length > 0 ? (
                                <div style={{ 
                                    filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                                    transform: 'perspective(1000px) rotateX(2deg)',
                                    transformStyle: 'preserve-3d'
                                }}>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <LineChart data={analytics.user_registration_trend}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis dataKey="date" />
                                            <YAxis />
                                            <RechartsTooltip 
                                                contentStyle={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                                }}
                                            />
                                            <Legend />
                                            <Line 
                                                type="monotone" 
                                                dataKey="users" 
                                                stroke="#8884d8"
                                                strokeWidth={3}
                                                dot={{ fill: '#8884d8', r: 5, strokeWidth: 2 }}
                                                activeDot={{ r: 7, strokeWidth: 2 }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px' }}>No data available</div>
                            )}
                        </Card>
                    </Col>

                    {/* Auction Status Distribution */}
                    <Col xs={24} md={12}>
                        <Card 
                            title="Auction Status Distribution"
                            style={{
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)'
                            }}
                        >
                            {analytics?.auction_status?.length > 0 ? (
                                <div style={{ 
                                    filter: 'drop-shadow(0 6px 12px rgba(0, 0, 0, 0.2))',
                                    transform: 'perspective(1000px) rotateX(5deg)',
                                    transformStyle: 'preserve-3d'
                                }}>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <PieChart>
                                            <defs>
                                                {analytics.auction_status.map((entry, index) => (
                                                    <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                                                        <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                                                        <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                                                    </linearGradient>
                                                ))}
                                            </defs>
                                            <Pie
                                                data={analytics.auction_status}
                                                dataKey="value"
                                                nameKey="name"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius={80}
                                                innerRadius={20}
                                                label
                                                paddingAngle={2}
                                            >
                                                {analytics.auction_status.map((entry, index) => (
                                                    <Cell 
                                                        key={`cell-${index}`} 
                                                        fill={`url(#gradient-${index})`}
                                                        stroke={entry.color}
                                                        strokeWidth={2}
                                                    />
                                                ))}
                                            </Pie>
                                            <RechartsTooltip 
                                                contentStyle={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                                }}
                                            />
                                            <Legend />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px' }}>No data available</div>
                            )}
                        </Card>
                    </Col>

                    {/* Top Categories */}
                    <Col xs={24} md={12}>
                        <Card 
                            title="Top Categories by Auction Count"
                            style={{
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                borderRadius: '8px',
                                background: 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)'
                            }}
                        >
                            {analytics?.top_categories?.length > 0 ? (
                                <div style={{ 
                                    filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))',
                                    transform: 'perspective(1000px) rotateX(2deg)',
                                    transformStyle: 'preserve-3d'
                                }}>
                                    <ResponsiveContainer width="100%" height={300}>
                                        <BarChart data={analytics.top_categories}>
                                            <defs>
                                                <linearGradient id="barGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                                    <stop offset="0%" stopColor="#82ca9d" stopOpacity={1} />
                                                    <stop offset="100%" stopColor="#52c41a" stopOpacity={0.8} />
                                                </linearGradient>
                                            </defs>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                                            <XAxis dataKey="name" />
                                            <YAxis />
                                            <RechartsTooltip 
                                                contentStyle={{
                                                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                                    border: '1px solid #ccc',
                                                    borderRadius: '4px',
                                                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                                                }}
                                            />
                                            <Legend />
                                            <Bar 
                                                dataKey="value" 
                                                fill="url(#barGradient)"
                                                radius={[8, 8, 0, 0]}
                                                stroke="#52c41a"
                                                strokeWidth={1}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px' }}>No data available</div>
                            )}
                        </Card>
                    </Col>

                    {/* Recent High Value Auctions */}
                    <Col xs={24} md={12}>
                        <Card title="Recent High Value Auctions">
                            {analytics?.recent_high_value_auctions?.length > 0 ? (
                                <Table
                                    dataSource={analytics.recent_high_value_auctions}
                                    rowKey="id"
                                    pagination={false}
                                    size="small"
                                    scroll={{ x: 'auto' }}
                                >
                                    <Table.Column 
                                        title="Title" 
                                        dataIndex="title"
                                        render={(title) => {
                                            if (!title) return 'N/A';
                                            const maxLength = 40;
                                            if (title.length > maxLength) {
                                                return (
                                                    <Tooltip title={title}>
                                                        <span>{title.substring(0, maxLength)}...</span>
                                                    </Tooltip>
                                                );
                                            }
                                            return title;
                                        }}
                                    />
                                    <Table.Column 
                                        title="Category" 
                                        dataIndex="category"
                                        render={(category) => (
                                            <Tag color="blue">
                                                {category || 'N/A'}
                                            </Tag>
                                        )}
                                    />
                                    <Table.Column 
                                        title="Lots" 
                                        dataIndex="lots_count"
                                        render={(count) => (
                                            <Tag color="purple">
                                                {count || 0}
                                            </Tag>
                                        )}
                                    />
                                    <Table.Column
                                        title="Total Value"
                                        dataIndex="total_value"
                                        render={(value) => (
                                            <Tag color="green">
                                                PKR {value?.toLocaleString() || '0'}
                                            </Tag>
                                        )}
                                    />
                                    <Table.Column
                                        title="Created"
                                        dataIndex="created_at"
                                        render={(date) => (
                                            <Tag color="default">
                                                {date ? new Date(date).toLocaleDateString() : 'N/A'}
                                            </Tag>
                                        )}
                                    />
                                </Table>
                            ) : (
                                <div style={{ textAlign: 'center', padding: '20px' }}>No data available</div>
                            )}
                        </Card>
            </Col>
        </Row>
            </Content>
            </Spin>
        </DashboardLayout>
    );
};

export default AdminDashboard; 